import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    LinearProgress,
    Stack
} from '@mui/material';
import { motion } from 'framer-motion';

const TeamPerformanceTable = ({ teamData }) => {
    // Extract dynamic performance keys from the first member or performance data
    const performanceKeys = teamData?.[0]?.performance ? Object.keys(teamData[0].performance) : [];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    borderRadius: '24px',
                    background: '#fff',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)'
                }}
            >
                <Typography variant="h5" fontWeight={800} sx={{ mb: 4 }}>
                    Team Performance Comparison
                </Typography>

                <TableContainer>
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'grey.50/50' }}>MEMBER</TableCell>
                                {performanceKeys.map((key) => (
                                    <TableCell
                                        key={key}
                                        sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'grey.50/50', textTransform: 'uppercase' }}
                                    >
                                        {teamData[0].performance[key].metric}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {teamData.map((member, index) => (
                                <TableRow
                                    key={member.id}
                                    component={motion.tr}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                    hover
                                    sx={{
                                        '&:hover': {
                                            bgcolor: 'primary.50/30 !important',
                                            '& td': { color: 'primary.main' }
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <TableCell>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    bgcolor: 'primary.50',
                                                    color: 'primary.main',
                                                    fontWeight: 800,
                                                    width: 40,
                                                    height: 40,
                                                    border: '2px solid transparent',
                                                    '&:hover': { borderColor: 'primary.200' },
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                {member.name.charAt(0)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={800}>
                                                    {member.name}
                                                </Typography>
                                                <Chip
                                                    label={member.role.replace(/_/g, ' ')}
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: '0.65rem',
                                                        textTransform: 'uppercase',
                                                        fontWeight: 900,
                                                        mt: 0.5,
                                                        bgcolor: 'grey.100',
                                                        color: 'text.secondary',
                                                        letterSpacing: 0.5
                                                    }}
                                                />
                                            </Box>
                                        </Stack>
                                    </TableCell>

                                    {performanceKeys.map((key) => {
                                        const metrics = member.performance?.[key];
                                        if (!metrics) return <TableCell key={key}>-</TableCell>;
                                        return (
                                            <TableCell key={key}>
                                                <Stack spacing={1}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2" fontWeight={800} sx={{ fontSize: '0.85rem' }}>
                                                            {metrics.achieved.toLocaleString()}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={800}>
                                                            {Math.round(metrics.percentage)}%
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ width: '100%' }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={metrics.percentage > 100 ? 100 : metrics.percentage}
                                                            sx={{
                                                                height: 6,
                                                                borderRadius: 3,
                                                                bgcolor: 'grey.100',
                                                                '& .MuiLinearProgress-bar': {
                                                                    borderRadius: 3,
                                                                    bgcolor:
                                                                        metrics.status.toLowerCase() === 'green'
                                                                            ? '#22c55e'
                                                                            : metrics.status.toLowerCase() === 'yellow'
                                                                            ? '#f59e0b'
                                                                            : '#ef4444'
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </motion.div>
    );
};

TeamPerformanceTable.propTypes = {
    teamData: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            role: PropTypes.string,
            performance: PropTypes.object
        })
    ).isRequired
};

export default TeamPerformanceTable;
